<script setup lang="ts">
import { computed } from 'vue'
import CreationOutlineIcon from 'vue-material-design-icons/CreationOutline.vue'
import ChevronDownIcon from 'vue-material-design-icons/ChevronDown.vue'
import ChevronUpIcon from 'vue-material-design-icons/ChevronUp.vue'
import { useReasoningDisclosure } from '@/composables/useReasoningDisclosure'
import { vStreamingText } from '@/components/chat/streamingText'

const props = withDefaults(defineProps<{
  liveReasoning?: string | null
  savedReasoning?: string | null
  reasoningActive?: boolean
  autoScrollCancelled?: boolean
  workingLabel?: string
  activeLabel?: string
  completedLabel?: string
}>(), { activeLabel: 'thinking…', completedLabel: 'thought for a moment' })

const {
  scrollContainer, displayReasoning, hasLiveReasoning, showReasoningPanel,
  showCollapsedReasoning, canCollapseReasoningPanel, expandReasoningPanel, collapseReasoningPanel,
} = useReasoningDisclosure({
  liveReasoning: () => props.liveReasoning,
  savedReasoning: () => props.savedReasoning,
  reasoningActive: () => Boolean(props.reasoningActive),
  autoScrollCancelled: () => Boolean(props.autoScrollCancelled),
})
const showControl = computed(() => Boolean(displayReasoning.value || props.workingLabel))
const reasoningLabel = computed(() => hasLiveReasoning.value ? props.activeLabel
  : displayReasoning.value ? props.completedLabel : props.workingLabel)
const panelText = computed(() => showReasoningPanel.value ? displayReasoning.value : '')
function toggleReasoning() {
  if (showCollapsedReasoning.value) expandReasoningPanel()
  else if (canCollapseReasoningPanel.value) collapseReasoningPanel()
}
// The message's alternative-height sizer mirrors the visible panel.
defineExpose({ panelText })
</script>

<template>
  <div class="prose-message__name-row">
    <slot name="identity" />
    <component
      :is="(showCollapsedReasoning || canCollapseReasoningPanel) ? 'button' : 'span'"
      v-if="showControl"
      class="prose-reasoning-toggle"
      :type="(showCollapsedReasoning || canCollapseReasoningPanel) ? 'button' : undefined"
      :aria-label="displayReasoning ? (showReasoningPanel ? 'Hide reasoning' : 'Show reasoning') : undefined"
      :role="!displayReasoning && workingLabel ? 'status' : undefined"
      @click="toggleReasoning"
    >
      <CreationOutlineIcon
        :size="13"
        class="prose-reasoning-toggle__spark"
        :class="{ 'prose-reasoning-toggle__spark--active': hasLiveReasoning || Boolean(workingLabel) }"
        aria-hidden="true"
      />
      <span class="prose-reasoning-toggle__label">{{ reasoningLabel }}</span>
      <ChevronUpIcon
        v-if="showReasoningPanel && canCollapseReasoningPanel"
        :size="13"
        class="prose-reasoning-toggle__chevron"
        aria-hidden="true"
      />
      <ChevronDownIcon
        v-else-if="showCollapsedReasoning"
        :size="13"
        class="prose-reasoning-toggle__chevron"
        aria-hidden="true"
      />
    </component>
  </div>
  <div v-if="panelText" ref="scrollContainer" v-streaming-text="panelText" class="prose-reasoning-panel" />
</template>

<style scoped>
.prose-message__name-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  margin-bottom: 4px;
}

/* Reasoning disclosure — a recessive, monospace affordance folded onto the
   identity line. Deliberately lighter than the name so it reads as incidental
   chrome, not a second header. Monospace (not italic) to avoid colliding with
   the italic = in-world narration convention used in message prose. */
.prose-reasoning-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
  margin-left: 0.35rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--theme-text-secondary);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.72rem;
  font-weight: 400;
  letter-spacing: 0.01em;
  line-height: 1;
  opacity: 0.72;
  text-align: start;
  appearance: none;
  transition: opacity 150ms ease, color 150ms ease;
}

button.prose-reasoning-toggle {
  cursor: pointer;
}

button.prose-reasoning-toggle:hover,
button.prose-reasoning-toggle:focus-visible {
  opacity: 1;
  color: var(--theme-on-surface);
}

.prose-reasoning-toggle__spark {
  display: inline-flex;
  align-items: center;
  color: var(--theme-primary);
  opacity: 0.7;
}

.prose-reasoning-toggle__spark--active {
  animation: prose-reasoning-pulse 1.6s ease-in-out infinite;
}

.prose-reasoning-toggle__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prose-reasoning-toggle__chevron {
  display: inline-flex;
  align-items: center;
  opacity: 0.6;
}

.prose-reasoning-panel {
  max-height: 160px;
  overflow-y: auto;
  margin: 0 0 0.5rem;
  padding: 0.1rem 0 0.1rem 0.7rem;
  border-left: 2px solid color-mix(in srgb, var(--theme-primary) 22%, var(--theme-border));
  color: color-mix(in srgb, var(--theme-on-surface) 55%, transparent);
  font-size: 0.9rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (prefers-reduced-motion: reduce) {
  .prose-reasoning-toggle__spark--active {
    animation: none;
  }
}

@keyframes prose-reasoning-pulse {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.85; }
}

</style>
